// Cuerpo rígido axisimétrico. Ver docs/theory/mass.md.

export interface RigidBody {
  /** Masa (kg). */
  readonly mass: number;
  /** Posición axial del CG desde la punta (m). */
  readonly cgX: number;
  /** Inercia transversal respecto al CG propio (kg·m²). */
  readonly iyy: number;
  /** Inercia axial, de rodadura (kg·m²). */
  readonly izz: number;
}

export const EMPTY_BODY: RigidBody = { mass: 0, cgX: 0, iyy: 0, izz: 0 };

/** Combina cuerpos con el teorema de Steiner. */
export function combine(bodies: readonly RigidBody[]): RigidBody {
  const mass = bodies.reduce((s, b) => s + b.mass, 0);
  if (mass === 0) return EMPTY_BODY;
  const cgX = bodies.reduce((s, b) => s + b.mass * b.cgX, 0) / mass;
  const iyy = bodies.reduce((s, b) => s + b.iyy + b.mass * (b.cgX - cgX) ** 2, 0);
  const izz = bodies.reduce((s, b) => s + b.izz, 0);
  return { mass, cgX, iyy, izz };
}

/** Traslada un cuerpo `dx` a lo largo del eje. */
export const shift = (b: RigidBody, dx: number): RigidBody => ({ ...b, cgX: b.cgX + dx });

/** Sustituye la masa conservando la distribución (escala las inercias proporcionalmente). */
export function withMass(b: RigidBody, mass: number): RigidBody {
  if (b.mass === 0) return { ...b, mass };
  const k = mass / b.mass;
  return { mass, cgX: b.cgX, iyy: b.iyy * k, izz: b.izz * k };
}

/** Tubo hueco o cilindro macizo (`innerRadius = 0`) con extremo delantero en x = 0. */
export function hollowCylinder(
  outerRadius: number,
  innerRadius: number,
  length: number,
  mass: number,
): RigidBody {
  const sq = outerRadius ** 2 + innerRadius ** 2;
  const solid = innerRadius === 0;
  return {
    mass,
    cgX: length / 2,
    izz: solid ? (mass * outerRadius ** 2) / 2 : (mass * sq) / 2,
    iyy: solid ? (mass * (3 * outerRadius ** 2 + length ** 2)) / 12 : (mass * (3 * sq + length ** 2)) / 12,
  };
}

/**
 * Sólido de revolución por rebanadas troncocónicas, extremo delantero en x = 0.
 * `thickness` define una cáscara delgada; con `filled` el componente es macizo.
 */
export function revolutionBody(
  radius: (x: number) => number,
  length: number,
  density: number,
  thickness: number,
  filled: boolean,
  slices = 400,
): RigidBody {
  const dx = length / slices;
  let mass = 0;
  let mx = 0;
  let iyyTip = 0;
  let izz = 0;
  for (let i = 0; i < slices; i++) {
    const x1 = i * dx;
    const r1 = radius(x1);
    const r2 = radius(x1 + dx);
    const xm = x1 + dx / 2;
    const rm = (r1 + r2) / 2;
    const dm = filled
      ? (density * Math.PI * dx * (r1 * r1 + r1 * r2 + r2 * r2)) / 3
      : density * thickness * Math.PI * (r1 + r2) * Math.hypot(dx, r2 - r1);
    mass += dm;
    mx += dm * xm;
    if (filled) {
      izz += (dm * rm * rm) / 2;
      iyyTip += dm * (xm * xm + (rm * rm) / 4 + (dx * dx) / 12);
    } else {
      izz += dm * rm * rm;
      iyyTip += dm * (xm * xm + (rm * rm) / 2 + (dx * dx) / 12);
    }
  }
  if (mass === 0) return EMPTY_BODY;
  const cgX = mx / mass;
  return { mass, cgX, izz, iyy: iyyTip - mass * cgX * cgX };
}
